import { useEffect } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { useChatStore } from '@/stores/chatStore';
import {
  SOCKET_EVENTS,
  type EntityType,
  type MessageNewEvent,
  type MessageEditedEvent,
  type MessageDeletedEvent,
  type MessageReactionEvent,
} from '@atlas/shared';

/**
 * Mount ONCE (in ChatProvider). Wires the four message:* socket events into the
 * chat store, keyed by entityKey carried on every payload.
 */
export function useChatSocketBridge(): void {
  const { socket } = useSocket();
  const receiveNew = useChatStore((s) => s.receiveNew);
  const receiveEdited = useChatStore((s) => s.receiveEdited);
  const receiveDeleted = useChatStore((s) => s.receiveDeleted);
  const receiveReaction = useChatStore((s) => s.receiveReaction);

  useEffect(() => {
    if (!socket) return;
    const onNew = (e: MessageNewEvent) => receiveNew(e.entityKey, e.message, e.tempId);
    const onEdited = (e: MessageEditedEvent) =>
      receiveEdited(e.entityKey, { messageId: e.messageId, body: e.body, mentions: e.mentions, editedAt: e.editedAt });
    const onDeleted = (e: MessageDeletedEvent) =>
      receiveDeleted(e.entityKey, { messageId: e.messageId, deletedAt: e.deletedAt });
    const onReaction = (e: MessageReactionEvent) =>
      receiveReaction(e.entityKey, { messageId: e.messageId, reactions: e.reactions });

    socket.on(SOCKET_EVENTS.MESSAGE_NEW, onNew);
    socket.on(SOCKET_EVENTS.MESSAGE_EDITED, onEdited);
    socket.on(SOCKET_EVENTS.MESSAGE_DELETED, onDeleted);
    socket.on(SOCKET_EVENTS.MESSAGE_REACTION, onReaction);
    return () => {
      socket.off(SOCKET_EVENTS.MESSAGE_NEW, onNew);
      socket.off(SOCKET_EVENTS.MESSAGE_EDITED, onEdited);
      socket.off(SOCKET_EVENTS.MESSAGE_DELETED, onDeleted);
      socket.off(SOCKET_EVENTS.MESSAGE_REACTION, onReaction);
    };
  }, [socket, receiveNew, receiveEdited, receiveDeleted, receiveReaction]);
}

/**
 * Join a thread room for the lifetime of a mounted thread view. Re-joins on
 * socket (re)connect — the server re-runs the EntityRegistry view check on each
 * join, so a revoked user is rejected (never joins).
 */
export function useThreadRoom(entityType: EntityType, entityId: string): void {
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket || !entityId) return;
    const join = () => socket.emit('message:join', { entityType, entityId });
    join();
    socket.on('connect', join); // rejoin after reconnect
    return () => {
      socket.off('connect', join);
      socket.emit('message:leave', { entityType, entityId });
    };
  }, [socket, entityType, entityId]);
}
